import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { authGuard } from './auth/auth.guard';
import { uploadRoleGuard } from './auth/upload-role.guard';
import { adminUsersRoleGuard } from './auth/admin-users-role.guard';
import { RoleLandingService } from './core/services/role-landing.service';
import { UserService } from './core/services/user.service';

export const routes: Routes = [
  // With keycloak-angular's onLoad: 'login-required' (see app.config.ts), the SPA
  // redirects unauthenticated visitors straight to Keycloak. After successful
  // login they land back at whatever URL they originally requested. The bare `''`
  // route resolves to a PER-ROLE default surface (ROLE-04) instead of always
  // /dashboard: the canActivate guard returns a UrlTree, the documented Angular
  // redirect pattern (mirrors auth.guard.ts's inject() usage). The component/
  // children never render because the guard always redirects.
  //
  // ADR-0031: roles are API-sourced (GET /api/users/me via UserService.
  // ensureLoaded()), so the guard is ASYNC and awaits the memoized bootstrap
  // before resolving the landing route — otherwise every login would race the
  // fetch and land on the empty-roles fallback. Guards run post-Keycloak-ready
  // (authGuard proves it), so the bearer token is always available here. All
  // inject() calls happen BEFORE the first await: Angular's DI context is lost
  // after an await inside a functional guard.
  { path: '', pathMatch: 'full',
    canActivate: [async () => {
      const landing = inject(RoleLandingService);
      const user = inject(UserService);
      const router = inject(Router);
      await user.ensureLoaded();
      return router.parseUrl(landing.defaultRouteFor(user.roles()));
    }],
    children: [] },
  { path: 'dashboard', canActivate: [authGuard],
    title: 'Dashboard',
    data: { breadcrumb: 'Dashboard' },
    loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent) },
  // Upload is reached THROUGH Submissions now (the standalone Upload nav item was
  // retired) — breadcrumb nests it under Submissions to match.
  // quick-260805-mpl / QA D9: hiding the nav item was never a gate — REVIEWER/VIEWER reached
  // /upload by direct URL and got the raw "Missing required X-School-Code header" backend string.
  // uploadRoleGuard runs AFTER authGuard (session first, then roles) and redirects unauthorized
  // roles to /dashboard with a clean message. The server-side 400 stays as defense in depth.
  { path: 'upload', canActivate: [authGuard, uploadRoleGuard],
    title: 'New upload',
    data: {
      breadcrumb: [
        { label: 'Uploads', url: '/submissions' },
        { label: 'Upload',      url: null },
      ],
    },
    loadChildren: () => import('./features/upload/upload.routes').then(m => m.UPLOAD_ROUTES) },
  // Phase 38 (USP-02, Pitfall 1): the standalone 'submissions/:id/history' route
  // that loaded HISTORY_ROUTES was REMOVED. With longest-prefix-first matching it
  // shadowed 'submissions/:id/:tab', so the history bookmark would otherwise keep
  // hitting the old standalone page instead of the unified History tab. Now
  // 'submissions/:id' → SUBMISSION_ROUTES → ':tab' host owns results | workflow |
  // history (the History content is rendered as the unified page's History tab).
  // The old /submissions/:id/result URL still resolves via the SUBMISSION_ROUTES
  // 'result' → 'results' redirect. Angular matches longest-prefix-first, so
  // 'submissions/:id' (here) stays ahead of bare 'submissions' below.
  { path: 'submissions/:id', canActivate: [authGuard],
    data: {
      breadcrumb: [
        { label: 'Uploads', url: '/submissions' },
        { label: 'Upload',  url: null },
      ],
    },
    loadChildren: () => import('./features/submission/submission.routes').then(m => m.SUBMISSION_ROUTES) },
  // Plan 06-18 / UPLD-10: bare /submissions LOCKED to MY_SUBMISSIONS_ROUTES.
  { path: 'submissions', canActivate: [authGuard],
    data: { breadcrumb: 'Uploads' },
    loadChildren: () => import('./features/my-submissions/my-submissions.routes').then(m => m.MY_SUBMISSIONS_ROUTES) },
  { path: 'approvals', canActivate: [authGuard],
    title: 'Approvals',
    data: { breadcrumb: 'Approvals' },
    loadChildren: () => import('./features/approval/approval.routes').then(m => m.approvalRoutes) },
  { path: 'settings/notifications', canActivate: [authGuard],
    title: 'Notifications',
    data: { breadcrumb: 'Notifications' },
    loadChildren: () => import('./features/notification-prefs/notification-prefs.routes').then(m => m.notificationPrefsRoutes) },
  { path: 'admin/idr/exceptions', canActivate: [authGuard],
    title: 'Identity exceptions',
    data: { breadcrumb: 'Identity exceptions' },
    loadChildren: () => import('./features/identity-exceptions/identity-exceptions.routes').then(m => m.IDENTITY_EXCEPTIONS_ROUTES) },
  // Plan 11-03 / ROLE-03: per-collection operational-status surface. Listed
  // BEFORE the bare /admin/collections route so Angular matches the more
  // specific :id/status path first (longest-prefix).
  { path: 'admin/collections/:id/status', canActivate: [authGuard],
    title: 'Collection status',
    data: {
      breadcrumb: [
        { label: 'Collections',       url: '/admin/collections' },
        { label: 'Collection status', url: null },
      ],
    },
    loadChildren: () => import('./features/collection-status/collection-status.routes').then(m => m.COLLECTION_STATUS_ROUTES) },
  { path: 'admin/collections', canActivate: [authGuard],
    title: 'Collections',
    data: { breadcrumb: 'Collections' },
    loadChildren: () => import('./features/collections/collections.routes').then(m => m.COLLECTIONS_ROUTES) },
  // Plan 13-06 / USR-11: org-admin user-management surface. authGuard proves the
  // session; adminUsersRoleGuard (QA SEC-01) redirects non-org-admin roles to
  // /dashboard as UX; RequireOrgAdmin + UserAdminScopeCheck server-side remain the enforcement.
  { path: 'admin/users', canActivate: [authGuard, adminUsersRoleGuard],
    title: 'Users',
    data: { breadcrumb: 'Users' },
    loadChildren: () => import('./features/users/users.routes').then(m => m.USERS_ROUTES) },
  { path: 'admin/org', canActivate: [authGuard],
    title: 'Org hierarchy',
    data: { breadcrumb: 'Org hierarchy' },
    loadChildren: () => import('./features/org-hierarchy/org-hierarchy.routes').then(m => m.ORG_HIERARCHY_ROUTES) },
  // quick-260624-e5c: the two scalable Records screens — Screen A Collections-by-Org
  // LIST at /records (landing) + Screen B per-(school,collection) DETAIL at
  // /records/:school/:collectionId. Replaces the retired 3-role records-view tree.
  // Listed BEFORE the `**` wildcard; per-screen scope is server-enforced.
  { path: 'records', canActivate: [authGuard],
    title: 'Collections',
    data: { breadcrumb: 'Collections' },
    loadChildren: () => import('./features/records/records.routes').then(m => m.RECORDS_ROUTES) },
  // Credential / claims diagnostic page (support tool). Direct-URL only — NOT
  // in the sidenav. Extracted out of the dashboard warmup branch so the old
  // claims table no longer flashes on every login.
  { path: 'claims', canActivate: [authGuard],
    title: 'Credentials',
    data: { breadcrumb: 'Claims' },
    loadComponent: () => import('./features/claims/claims.component').then(m => m.ClaimsComponent) },
  // Phase 47 (LKR-10 / LKR-12, D-15): embedded Looker dashboards. Lazy-loaded so the
  // @looker/embed-sdk stays out of the initial bundle. Plural `dashboards` — the singular
  // `dashboard` path above is the NEPF home. The API's 403 is the real scope gate; the nav
  // item arrives in plan 47-04.
  { path: 'dashboards', canActivate: [authGuard],
    title: 'Dashboards',
    data: { breadcrumb: 'Dashboards' },
    loadComponent: () => import('./features/dashboards/dashboards-page.component').then(m => m.DashboardsPageComponent) },
  // NAV-08: a real 404 page replaces the old silent `**` → `''` redirect (which
  // yanked unknown URLs to the role-landing surface with no feedback). Must stay
  // LAST so every concrete route above matches first.
  { path: '**',
    title: 'Page not found',
    data: { breadcrumb: 'Page not found' },
    loadComponent: () => import('./features/not-found/not-found.component').then(m => m.NotFoundComponent) },
];
